// 투자계획등록 화면 (좌: 그리드 / 우: 그래프)
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { fetchAccounts } from '../../api/accounts';
import { fetchStocks } from '../../api/stocks';
import {
  fetchInvestPlan, fetchInvestPlanSummary,
  saveSectorPlan, saveStockPlan,
  fetchAvailableSectors, initDefaultPlan,
} from '../../api/invest_plan';
import './InvestPlanPage.css';

const fNum  = v  => (v != null && v !== 0 ? Number(v).toLocaleString('ko-KR') : '');
const fPct  = v  => (v != null && v !== '' ? Number(v).toFixed(1) : '');
const fPctTarget = (curr, plan) => plan > 0 ? `${Number(curr / plan * 100).toFixed(1)}%` : '';

const CHART_COLORS = [
  '#4a90e2','#e67e22','#27ae60','#e74c3c',
  '#9b59b6','#1abc9c','#f39c12','#34495e',
  '#16a085','#d35400','#2980b9','#8e44ad',
];

// 전체 통합 현황 탭 - 목표/현재 도넛 공통 색상 (같은 섹터는 같은 색)
const SECTOR_COLORS = [
  '#4A90D9', '#52C41A', '#FFA500',
  '#FF4D4F', '#722ED1', '#13C2C2',
  '#EB2F96', '#FA8C16', '#A0D911',
  '#1890FF',
];

// ── 바 차트 범례 (순서 고정) ──
const BAR_LEGEND_ITEMS = [
  { label: '목표비율',    color: '#4A90D9' },
  { label: '현재비율',    color: '#FFA500' },
  { label: '시세반영비율', color: '#52C41A' },
];
const BarLegend = () => (
  <div style={{ display: 'flex', gap: 16, justifyContent: 'center', padding: '4px 0', fontSize: 11 }}>
    {BAR_LEGEND_ITEMS.map((item, i) => (
      <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <svg width="14" height="10"><rect width="14" height="10" rx="2" fill={item.color} /></svg>
        {item.label}
      </span>
    ))}
  </div>
);

// ── 차트 툴팁 (모듈 레벨, 섹터/종목 공통) ──
const BarTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p   = payload.find(x => x.dataKey === 'plan_ratio');
  const c   = payload.find(x => x.dataKey === 'current_ratio');
  const pr  = payload.find(x => x.dataKey === 'price_reflected_ratio');
  const diff = (p && c) ? (p.value - c.value) : null;
  return (
    <div style={{ background: 'white', border: '1px solid #ddd', padding: '6px 10px', fontSize: 11, borderRadius: 4 }}>
      <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>
      {p  && <p style={{ color: '#4A90D9', margin: '2px 0' }}>목표비율: {Number(p.value).toFixed(1)}%</p>}
      {c  && <p style={{ color: '#FFA500', margin: '2px 0' }}>현재비율: {Number(c.value).toFixed(1)}%</p>}
      {pr && <p style={{ color: '#52C41A', margin: '2px 0' }}>시세반영비율: {Number(pr.value).toFixed(1)}%</p>}
      {diff !== null && (
        <p style={{ color: diff >= 0 ? '#4A90D9' : '#e74c3c', margin: '2px 0' }}>
          차이(목표-현재): {diff >= 0 ? '+' : ''}{Number(diff).toFixed(1)}%
        </p>
      )}
    </div>
  );
};

const InvestPlanPage = ({ isActive }) => {
  const { isAdmin } = useAuth();

  const [accounts,        setAccounts]        = useState([]);
  const [selAcct,         setSelAcct]         = useState('');
  const [activeTab,       setActiveTab]       = useState('account');
  const [plan,            setPlan]            = useState(null);
  const [editRatios,      setEditRatios]      = useState({});
  const [editWeights,     setEditWeights]     = useState({});
  const [editSectorMemos, setEditSectorMemos] = useState({});
  const [editStockMemos,  setEditStockMemos]  = useState({});
  const [expandedSec,     setExpandedSec]     = useState(new Set());
  const [summary,         setSummary]         = useState(null);
  const [expandedSum,     setExpandedSum]     = useState(new Set());
  const [userStocks,      setUserStocks]      = useState([]);
  const [infoOpen,        setInfoOpen]        = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [busy,            setBusy]            = useState(false);
  const [message,         setMessage]         = useState({ text: '', type: '' });
  const [addSecOpen,      setAddSecOpen]      = useState(false);
  const [addSecForm,      setAddSecForm]      = useState({ sector_code_id: '', plan_ratio: '' });
  const [availSecs,       setAvailSecs]       = useState([]);
  const [addStkPopup,     setAddStkPopup]     = useState(null);
  const [addStkForm,      setAddStkForm]      = useState({ ticker: '', weight: '100' });
  const [chartViewAcct,   setChartViewAcct]   = useState('sector');
  const [chartViewSum,    setChartViewSum]    = useState('sector');
  const [excludeCash,     setExcludeCash]     = useState(false);

  // 컬럼1 너비 드래그
  const col1WidthRef = useRef(300);
  const [col1Width,  setCol1Width]  = useState(300);

  // 좌우 분할 드래그
  const containerRef  = useRef(null);
  const leftWidthRef  = useRef(60);
  const [leftWidth,     setLeftWidth]     = useState(60);
  const [isDragging,    setIsDragging]    = useState(false);
  const [userAdjusted,  setUserAdjusted]  = useState(false);

  const showMsg = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3500);
  }, []);

  // ── 컬럼1 너비 드래그 핸들러 ──
  const handleCol1Resize = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = col1WidthRef.current;
    const onMove = (me) => {
      const newW = Math.max(150, startW + me.clientX - startX);
      col1WidthRef.current = newW;
      setCol1Width(newW);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // ── 좌우 분할 드래그 핸들러 ──
  const handleResizerMouseDown = useCallback((e) => {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX      = e.clientX;
    const startPct    = leftWidthRef.current;
    const totalWidth  = container.getBoundingClientRect().width;
    const onMove = (me) => {
      const dx      = me.clientX - startX;
      const newPct  = startPct + (dx / totalWidth) * 100;
      const clamped = Math.min(80, Math.max(30, newPct));
      leftWidthRef.current = clamped;
      setLeftWidth(clamped);
    };
    const onUp = () => {
      setIsDragging(false);
      setUserAdjusted(true);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    fetchAccounts().then(d => setAccounts(d || [])).catch(() => {});
    fetchStocks().then(d => setUserStocks(d || [])).catch(() => {});
  }, [isActive]);

  const loadPlan = useCallback(async (acct) => {
    const name = acct !== undefined ? acct : selAcct;
    if (!name) return;
    setLoading(true);
    try {
      const data = await fetchInvestPlan(name);
      setPlan(data);
      setEditRatios({});
      setEditWeights({});
      setEditSectorMemos({});
      setEditStockMemos({});
      setExpandedSec(new Set());
    } catch {
      showMsg('조회에 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, [selAcct, showMsg]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchInvestPlanSummary();
      setSummary(data);
      setExpandedSum(new Set());
    } catch {
      showMsg('통합 조회 실패', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMsg]);

  useEffect(() => {
    if (!isActive || activeTab !== 'summary') return;
    loadSummary();
  }, [activeTab, isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 편집 반영 섹터 목록 ──
  const computedSectors = useMemo(() => {
    if (!plan) return [];
    const total = plan.total_amount || 0;
    return plan.sectors.map(sec => {
      const sid        = sec.sector_code_id;
      const rStr       = editRatios[sid];
      const ratio      = rStr !== undefined ? (parseFloat(rStr) || 0) : sec.plan_ratio;
      const planAmt    = total * ratio / 100;
      const secCurrAmt = sec.current_amount || 0;

      const stkEdited = sec.stocks.map(stk => {
        const wKey = `${sid}|${stk.ticker}`;
        const wStr = editWeights[wKey];
        return { ...stk, weight: wStr !== undefined ? (parseFloat(wStr) || 0) : stk.weight, _wEdited: wStr !== undefined };
      });
      const wTotal = stkEdited.reduce((s, st) => s + st.weight, 0);
      const stocks = stkEdited.map(stk => {
        const stkPlan = wTotal > 0 ? planAmt * stk.weight / wTotal : 0;
        const inSec   = secCurrAmt > 0 ? stk.current_amount / secCurrAmt * 100 : 0;
        return {
          ...stk,
          plan_ratio_in_sector: parseFloat(inSec.toFixed(1)),
          plan_amount:  Math.round(stkPlan),
          diff_amount:  Math.round(stk.current_amount - stkPlan),
        };
      });
      return {
        ...sec,
        plan_ratio:   ratio,
        plan_amount:  Math.round(planAmt),
        diff_amount:  Math.round(sec.current_amount - planAmt),
        weight_total: wTotal,
        stocks,
        _rEdited: rStr !== undefined,
      };
    });
  }, [plan, editRatios, editWeights]);

  const ratioTotal  = computedSectors.reduce((s, sec) => s + sec.plan_ratio, 0);
  const hasDirty    = Object.keys(editRatios).length > 0 || Object.keys(editWeights).length > 0
                    || Object.keys(editSectorMemos).length > 0 || Object.keys(editStockMemos).length > 0;
  const ratioExact  = Math.abs(ratioTotal - 100) < 0.05;
  const ratioCls    = ratioTotal === 0  ? ''
                    : ratioExact        ? 'ip-ratio-ok'
                    : ratioTotal > 100  ? 'ip-ratio-over'
                    : 'ip-ratio-under';
  const ratioSuffix = ratioTotal > 100 ? ' ⚠️' : '';

  const toggleSec = sid => setExpandedSec(prev => {
    const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
  });
  const toggleSum = sid => setExpandedSum(prev => {
    const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n;
  });

  // ── 저장 ──
  const handleSave = async () => {
    if (!hasDirty) { showMsg('변경된 데이터가 없습니다.', 'info'); return; }
    setBusy(true);
    try {
      const allSectorSids = new Set([...Object.keys(editRatios), ...Object.keys(editSectorMemos)]);
      if (allSectorSids.size > 0) {
        const sectors = [...allSectorSids].map(sid => {
          const orig = plan.sectors.find(s => s.sector_code_id === sid);
          return {
            sector_code_id: sid,
            plan_ratio: editRatios[sid] !== undefined ? (parseFloat(editRatios[sid]) || 0) : (orig?.plan_ratio || 0),
            memo: editSectorMemos[sid] !== undefined ? editSectorMemos[sid] : (orig?.memo || ''),
          };
        });
        await saveSectorPlan({ account_name: selAcct, sectors });
      }
      const secMap = {};
      const allStockKeys = new Set([...Object.keys(editWeights), ...Object.keys(editStockMemos)]);
      for (const key of allStockKeys) {
        const pi = key.indexOf('|');
        const sid = key.slice(0, pi), ticker = key.slice(pi + 1);
        if (!secMap[sid]) secMap[sid] = [];
        const orig = plan.sectors.find(s => s.sector_code_id === sid)?.stocks.find(st => st.ticker === ticker);
        secMap[sid].push({
          ticker,
          weight: editWeights[key] !== undefined ? (parseFloat(editWeights[key]) || 0) : (orig?.weight || 0),
          memo: editStockMemos[key] !== undefined ? editStockMemos[key] : (orig?.memo || ''),
        });
      }
      for (const [sid, stocks] of Object.entries(secMap)) {
        await saveStockPlan({ account_name: selAcct, sector_code_id: sid, stocks });
      }
      showMsg('저장되었습니다.');
      await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '저장 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelSector = async (sid, sectorName) => {
    if (!window.confirm(`${sectorName} 섹터 계획을 삭제합니다.\n하위 종목 가중치도 함께 삭제됩니다. 계속하시겠습니까?`)) return;
    setBusy(true);
    try {
      const sec = plan.sectors.find(s => s.sector_code_id === sid);
      if (sec?.stocks.length > 0) {
        await saveStockPlan({
          account_name: selAcct, sector_code_id: sid,
          stocks: sec.stocks.map(st => ({ ticker: st.ticker, weight: 0, memo: '' })),
        });
      }
      await saveSectorPlan({ account_name: selAcct, sectors: [{ sector_code_id: sid, plan_ratio: 0, memo: '' }] });
      showMsg('삭제되었습니다.');
      await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '삭제 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDelStock = async (sid, ticker, stockName) => {
    if (!window.confirm(`${stockName || ticker} 종목 계획을 삭제합니다.`)) return;
    setBusy(true);
    try {
      await saveStockPlan({ account_name: selAcct, sector_code_id: sid, stocks: [{ ticker, weight: 0, memo: '' }] });
      showMsg('삭제되었습니다.');
      await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '삭제 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const openAddSector = async () => {
    if (!selAcct) return;
    setAddSecOpen(true);
    setAddSecForm({ sector_code_id: '', plan_ratio: '' });
    setAvailSecs([]);
    try {
      const data = await fetchAvailableSectors(selAcct);
      setAvailSecs(data.sectors || []);
    } catch {
      setAvailSecs([]);
    }
  };

  const handleAddSector = async () => {
    if (!addSecForm.sector_code_id) { showMsg('섹터를 선택하세요.', 'error'); return; }
    setBusy(true);
    try {
      await saveSectorPlan({
        account_name: selAcct,
        sectors: [{ sector_code_id: addSecForm.sector_code_id, plan_ratio: parseFloat(addSecForm.plan_ratio) || 0, memo: '' }],
      });
      showMsg('섹터가 추가되었습니다.');
      setAddSecOpen(false);
      await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '추가 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleAddStock = async () => {
    if (!addStkForm.ticker) { showMsg('종목을 선택하세요.', 'error'); return; }
    setBusy(true);
    try {
      await saveStockPlan({
        account_name:   addStkPopup.account_name,
        sector_code_id: addStkPopup.sector_code_id,
        stocks: [{ ticker: addStkForm.ticker, weight: parseFloat(addStkForm.weight) || 100, memo: '' }],
      });
      showMsg('종목이 추가되었습니다.');
      const sid = addStkPopup.sector_code_id;
      setAddStkPopup(null);
      setAddStkForm({ ticker: '', weight: '100' });
      setExpandedSec(prev => { const n = new Set(prev); n.add(sid); return n; });
      await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '추가 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleInitDefault = async () => {
    if (!window.confirm('user1의 기본 투자계획 데이터를 생성합니다.\n(이미 존재하는 데이터는 건드리지 않습니다)')) return;
    setBusy(true);
    try {
      const res = await initDefaultPlan({ user_id: 'user1' });
      showMsg(res.message || '생성되었습니다.');
      if (selAcct) await loadPlan();
    } catch (e) {
      showMsg(e.response?.data?.detail || '생성 실패', 'error');
    } finally {
      setBusy(false);
    }
  };

  const availableStocks = useMemo(() => {
    if (!addStkPopup || !plan) return [];
    const sec = plan.sectors.find(s => s.sector_code_id === addStkPopup.sector_code_id);
    const registered = new Set(sec?.stocks.map(st => st.ticker) || []);
    return userStocks.filter(st => !registered.has(st.ticker));
  }, [addStkPopup, plan, userStocks]);

  // ── 안내문 박스 ──
  const infoBox = (
    <div className="ip-info-box">
      <div className="ip-info-header">
        <span>ℹ 투자계획 설정 방법</span>
        <button className="ip-info-toggle" onClick={() => setInfoOpen(p => !p)}>
          {infoOpen ? '접기' : '펼치기'}
        </button>
      </div>
      {infoOpen && (
        <div className="ip-info-body">
          <p className="ip-info-step">[1단계] 섹터별 목표비율 설정</p>
          <p>계좌 총투자금액 대비 각 섹터에 투자할 비율을 설정하세요.</p>
          <p className="ip-info-note">현금 보유 계획이 있다면 합계가 100% 미만이어도 됩니다.</p>
          <p className="ip-info-step">[2단계] 섹터 내 종목별 가중치 설정</p>
          <p>각 종목의 가중치를 설정하세요. (기본값: 100)</p>
          <p className="ip-info-note">가중치 합계와 무관하게 비율로 환산하여 안분합니다.</p>
          <p className="ip-info-note">예) 가중치 100:200이면 섹터 배분금액을 1:2로 나눔</p>
        </div>
      )}
    </div>
  );

  // 전체 통합 현황(summary.sectors) 전체 목록 기준 고정 색상 인덱스
  // (excludeCash 필터링 후에도 같은 섹터는 항상 같은 색)
  const getSectorColor = useCallback((sectorName) => {
    const allSecs = summary?.sectors || [];
    const idx = allSecs.findIndex(s => s.sector_name === sectorName);
    return SECTOR_COLORS[(idx >= 0 ? idx : 0) % SECTOR_COLORS.length];
  }, [summary]);

  // ── 섹터 차트: 도넛(1개 또는 2개) + 막대 1개 (수직 배치) ──
  // isSummary: 전체 통합 현황 탭일 때 목표/현재 도넛을 좌우로 나란히 표시
  const renderSectorCharts = (sectors, totalAmt, isSummary = false) => {
    const activeSecs = excludeCash ? sectors.filter(s => !s.is_cash_asset) : sectors;
    const planData = activeSecs
      .map(sec => ({ name: sec.sector_name, value: parseFloat(fPct(sec.plan_ratio)) }))
      .filter(d => d.value > 0);
    const barData = activeSecs.map(sec => ({
      name:                  sec.sector_name,
      plan_ratio:            parseFloat(fPct(sec.plan_ratio)),
      current_ratio:         parseFloat(fPct(sec.current_ratio)),
      price_reflected_ratio: parseFloat(fPct(sec.price_reflected_ratio || 0)),
    }));

    const barChart = (
      <>
        {/* 막대 - 목표 vs 현재 vs 시세반영 비율 */}
        <div className="ip-chart-title">목표 vs 현재 vs 시세반영 비율</div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={barData} margin={{ top: 4, right: 8, left: 0, bottom: 44 }} barCategoryGap="40%" barGap={3}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} width={36} />
            <Tooltip content={<BarTooltip />} />
            <Legend content={<BarLegend />} />
            <Bar dataKey="plan_ratio"            name="목표비율"    fill="#4A90D9" barSize={10} />
            <Bar dataKey="current_ratio"         name="현재비율"    fill="#FFA500" barSize={10} />
            <Bar dataKey="price_reflected_ratio" name="시세반영비율" fill="#52C41A" barSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </>
    );

    if (!isSummary) {
      return (
        <>
          {/* 도넛 - 섹터별 목표비율 (범례 없음, labelLine만 사용) */}
          <div className="ip-chart-title">섹터별 목표비율 (도넛 100% 기준)</div>
          <div style={{ textAlign: 'center', fontSize: 11, color: '#888', marginBottom: 2 }}>※ 도넛 비율은 실제 목표비율과 다를 수 있습니다.</div>
          <ResponsiveContainer width="100%" height={270}>
            <PieChart>
              <Pie
                data={planData}
                cx="50%" cy="50%"
                innerRadius={50} outerRadius={80}
                dataKey="value"
                label={({ name, percent }) =>
                  percent > 0.03 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''
                }
                labelLine={{ stroke: '#aaa', strokeWidth: 1 }}
              >
                {planData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, name) => [`${Number(v).toFixed(1)}%`, name]} />
            </PieChart>
          </ResponsiveContainer>

          {barChart}
        </>
      );
    }

    // ── 전체 통합 현황 탭: 목표비율 도넛 + 현재비율(매수원금 기준) 도넛 좌우 배치 ──
    const currentData = activeSecs
      .filter(s => (s.current_amount || 0) > 0)
      .map(s => ({ name: s.sector_name, value: s.current_amount }));
    const currentTotal = currentData.reduce((s, d) => s + d.value, 0);

    return (
      <>
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          {/* 왼쪽 도넛: 섹터별 목표비율 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ip-chart-title" style={{ fontSize: 12 }}>섹터별 목표비율</div>
            <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginBottom: 2 }}>
              ※ 실제 목표비율 합계와 다를 수 있습니다.
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={planData}
                  cx="50%" cy="50%"
                  innerRadius={40} outerRadius={65}
                  dataKey="value"
                  label={({ name, percent }) =>
                    percent > 0.03 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''
                  }
                  labelLine={{ stroke: '#aaa', strokeWidth: 1 }}
                >
                  {planData.map((d, i) => <Cell key={i} fill={getSectorColor(d.name)} />)}
                </Pie>
                <Tooltip formatter={(v, name) => [`${name}: ${Number(v).toFixed(1)}%`, '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* 오른쪽 도넛: 섹터별 현재 투자실적 (매수원금 기준) */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ip-chart-title" style={{ fontSize: 12 }}>섹터별 현재비율</div>
            <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginBottom: 2 }}>
              (매수원금 기준)
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={currentData}
                  cx="50%" cy="50%"
                  innerRadius={40} outerRadius={65}
                  dataKey="value"
                  label={({ name, percent }) =>
                    percent > 0.03 ? `${name}: ${(percent * 100).toFixed(1)}%` : ''
                  }
                  labelLine={{ stroke: '#aaa', strokeWidth: 1 }}
                >
                  {currentData.map((d, i) => <Cell key={i} fill={getSectorColor(d.name)} />)}
                </Pie>
                <Tooltip formatter={(v, name) => [
                  `${name}: ${fNum(v)}원 (${currentTotal > 0 ? (v / currentTotal * 100).toFixed(1) : '0.0'}%)`, '',
                ]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {barChart}
      </>
    );
  };

  // ── 종목 차트: 막대 1개 ──
  const renderStockCharts = (sectors, totalAmt) => {
    const stockData = [];
    sectors.forEach(sec => {
      if (excludeCash && sec.is_cash_asset) return;
      (sec.stocks || []).forEach(stk => {
        if (excludeCash && stk.is_cash_asset) return;
        stockData.push({
          name:                  stk.stock_name || stk.ticker,
          plan_ratio:            totalAmt > 0 ? parseFloat((stk.plan_amount    / totalAmt * 100).toFixed(1)) : 0,
          current_ratio:         totalAmt > 0 ? parseFloat((stk.current_amount / totalAmt * 100).toFixed(1)) : 0,
          price_reflected_ratio: parseFloat(fPct(stk.price_reflected_ratio || 0)),
        });
      });
    });
    return (
      <>
        <div className="ip-chart-title">종목별 목표 vs 현재 vs 시세반영 비율</div>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart data={stockData} margin={{ top: 4, right: 12, left: 0, bottom: 60 }} barCategoryGap="40%" barGap={3}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" angle={-45} textAnchor="end" interval={0} tick={{ fontSize: 10 }} />
            <YAxis tickFormatter={v => `${v}%`} tick={{ fontSize: 11 }} width={36} />
            <Tooltip content={<BarTooltip />} />
            <Legend content={<BarLegend />} />
            <Bar dataKey="plan_ratio"            name="목표비율"    fill="#4A90D9" barSize={10} />
            <Bar dataKey="current_ratio"         name="현재비율"    fill="#FFA500" barSize={10} />
            <Bar dataKey="price_reflected_ratio" name="시세반영비율" fill="#52C41A" barSize={10} />
          </BarChart>
        </ResponsiveContainer>
      </>
    );
  };

  // ── 우측 차트 패널 ──
  const renderChartPanel = () => {
    const isAcct      = activeTab === 'account';
    const chartSecs   = isAcct ? computedSectors : (summary?.sectors || []);
    const chartTotal  = isAcct ? (plan?.total_amount || 0) : (summary?.total_amount || 0);
    const chartView   = isAcct ? chartViewAcct : chartViewSum;
    const setChartView = isAcct ? setChartViewAcct : setChartViewSum;

    return (
      <div className="ip-chart-panel">
        <div className="ip-chart-toggle">
          <button className={`ip-chart-btn${chartView === 'sector' ? ' active' : ''}`} onClick={() => setChartView('sector')}>섹터</button>
          <button className={`ip-chart-btn${chartView === 'stock'  ? ' active' : ''}`} onClick={() => setChartView('stock')}>종목</button>
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={excludeCash} onChange={e => setExcludeCash(e.target.checked)} style={{ cursor: 'pointer' }} />
            현금성자산 제외
          </label>
        </div>
        {chartSecs.length === 0 || (excludeCash && chartSecs.every(s => s.is_cash_asset)) ? (
          <div className="ip-chart-empty">계획 데이터가 없습니다.</div>
        ) : (
          chartView === 'sector'
            ? renderSectorCharts(chartSecs, chartTotal, !isAcct)
            : renderStockCharts(chartSecs, chartTotal)
        )}
      </div>
    );
  };

  // ── 좌측: 계좌별 그리드 ──
  const renderAccountGrid = () => {
    const planAmtTotal = computedSectors.reduce((s, sec) => s + sec.plan_amount, 0);
    const currAmtTotal = computedSectors.reduce((s, sec) => s + sec.current_amount, 0);
    const diffAmtTotal = currAmtTotal - planAmtTotal;

    return (
      <>
        {/* 조회조건 행 */}
        <div className="ip-controls">
          <div className="ip-ctrl-left">
            <select
              value={selAcct}
              onChange={e => {
                setSelAcct(e.target.value);
                setPlan(null);
                setEditRatios({}); setEditWeights({});
                setEditSectorMemos({}); setEditStockMemos({});
              }}
            >
              <option value="">-- 계좌 선택 --</option>
              {accounts.map(a => <option key={a.id} value={a.account_name}>{a.account_name}</option>)}
            </select>
            <button className="btn btn-sm btn-primary" style={{ marginLeft: 6 }}
              onClick={() => loadPlan()} disabled={loading || !selAcct}>
              조회
            </button>
            <button className="btn btn-sm btn-primary" style={{ marginLeft: -2 }}
              onClick={handleSave} disabled={busy || !hasDirty}>
              저장
            </button>
          </div>
          {plan && (
            <>
              <div className="ip-ctrl-sep" />
              <div className="ip-ctrl-right">
                <span className="ip-ctrl-item">총투자금액 <strong>{fNum(plan.total_amount)}원</strong></span>
                <span className="ip-ctrl-item">매수잔액 <strong>{fNum(plan.buy_amount)}</strong></span>
                <span className="ip-ctrl-item">현금 <strong>{fNum(plan.cash_amount)}</strong></span>
                <span className="ip-ctrl-item">
                  목표비율합계{' '}
                  <strong className={ratioCls}>{fPct(ratioTotal)}%{ratioSuffix}</strong>
                </span>
              </div>
            </>
          )}
        </div>

        {infoBox}

        {!selAcct && <div className="ip-empty">계좌를 선택한 후 조회 버튼을 클릭하세요.</div>}
        {selAcct && loading && <div className="ip-empty">조회 중...</div>}
        {selAcct && !loading && !plan && <div className="ip-empty">조회 버튼을 클릭하세요.</div>}

        {plan && (
          <>
            <div className="ip-grid-wrap">
              <table className="ip-grid">
                <colgroup>
                  <col style={{ width: col1Width }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 82 }} />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 100 }} />
                  <col />
                  <col style={{ width: 48 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', position: 'relative' }}>
                      섹터명
                      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', userSelect: 'none' }}
                           onMouseDown={handleCol1Resize} />
                    </th>
                    <th>목표비율%</th>
                    <th>목표금액</th>
                    <th>현재금액</th>
                    <th>현투자비율</th>
                    <th>차이금액</th>
                    <th>목표대비투자비율</th>
                    <th style={{ textAlign: 'left' }}>메모</th>
                    <th className="th-center">삭제</th>
                  </tr>
                </thead>
                <tbody>
                  {computedSectors.length === 0 && (
                    <tr><td colSpan={9} className="ip-empty">등록된 섹터가 없습니다.</td></tr>
                  )}
                  {computedSectors.map(sec => {
                    const sid           = sec.sector_code_id;
                    const isExp         = expandedSec.has(sid);
                    const secMemoVal    = editSectorMemos[sid] !== undefined ? editSectorMemos[sid] : (sec.memo || '');
                    const secMemoEdited = editSectorMemos[sid] !== undefined;
                    return (
                      <React.Fragment key={sid}>
                        <tr className={`ip-sec-row${isExp ? ' expanded' : ''}`}>
                          <td onClick={() => toggleSec(sid)} style={{ cursor: 'pointer' }}>
                            {isExp ? '▼' : '▶'} {sec.sector_name}
                          </td>
                          <td className={`ip-sec-edit${sec._rEdited ? ' ip-dirty' : ''}`}>
                            <input
                              className="ip-edit-input"
                              style={{ width: 80 }}
                              value={editRatios[sid] !== undefined ? editRatios[sid] : String(sec.plan_ratio)}
                              onChange={e => setEditRatios(p => ({ ...p, [sid]: e.target.value.replace(/[^0-9.]/g, '') }))}
                            />
                          </td>
                          <td>{fNum(sec.plan_amount)}</td>
                          <td>{fNum(sec.current_amount)}</td>
                          <td>{fPct(sec.current_ratio)}</td>
                          <td>{fNum(sec.diff_amount)}</td>
                          <td>{fPctTarget(sec.current_amount, sec.plan_amount)}</td>
                          <td className={`ip-sec-edit${secMemoEdited ? ' ip-dirty' : ''}`}>
                            <input
                              className="ip-edit-input"
                              style={{ textAlign: 'left', fontWeight: 'normal', fontSize: 11 }}
                              value={secMemoVal}
                              onChange={e => setEditSectorMemos(p => ({ ...p, [sid]: e.target.value }))}
                            />
                          </td>
                          <td className="td-center">
                            <button className="ip-del-btn" title="섹터 삭제"
                              onClick={() => handleDelSector(sid, sec.sector_name)} disabled={busy}>🗑️</button>
                          </td>
                        </tr>

                        {isExp && (
                          <>
                            <tr className="ip-stk-hdr">
                              <td className="ip-stk-name">종목명 (티커)</td>
                              <td>가중치</td>
                              <td>목표금액</td>
                              <td>현재금액</td>
                              <td>섹터내%</td>
                              <td>차이금액</td>
                              <td>목표대비%</td>
                              <td>메모</td>
                              <td></td>
                            </tr>
                            {sec.stocks.map(stk => {
                              const wKey          = `${sid}|${stk.ticker}`;
                              const stkMemoVal    = editStockMemos[wKey] !== undefined ? editStockMemos[wKey] : (stk.memo || '');
                              const stkMemoEdited = editStockMemos[wKey] !== undefined;
                              return (
                                <tr key={stk.ticker} className="ip-stk-row">
                                  <td className="ip-stk-name">
                                    <span className="ip-stk-indent">└</span>
                                    {stk.stock_name || stk.ticker}
                                    <span style={{ color: '#999', fontSize: 10, marginLeft: 4 }}>({stk.ticker})</span>
                                  </td>
                                  <td className={`ip-stk-edit${stk._wEdited ? ' ip-dirty' : ''}`}>
                                    <input
                                      className="ip-edit-input"
                                      style={{ width: 60, fontSize: '0.9em' }}
                                      value={editWeights[wKey] !== undefined ? editWeights[wKey] : String(Math.round(stk.weight))}
                                      onChange={e => setEditWeights(p => ({ ...p, [wKey]: e.target.value.replace(/[^0-9.]/g, '') }))}
                                    />
                                  </td>
                                  <td>{fNum(stk.plan_amount)}</td>
                                  <td>{fNum(stk.current_amount)}</td>
                                  <td>{fPct(stk.plan_ratio_in_sector)}</td>
                                  <td>{fNum(stk.diff_amount)}</td>
                                  <td>{fPctTarget(stk.current_amount, stk.plan_amount)}</td>
                                  <td className={`ip-stk-edit${stkMemoEdited ? ' ip-dirty' : ''}`} style={{ textAlign: 'left' }}>
                                    <input
                                      className="ip-edit-input"
                                      style={{ textAlign: 'left', fontSize: 11 }}
                                      value={stkMemoVal}
                                      onChange={e => setEditStockMemos(p => ({ ...p, [wKey]: e.target.value }))}
                                    />
                                  </td>
                                  <td className="td-center">
                                    <button className="ip-del-btn" title="종목 삭제"
                                      onClick={() => handleDelStock(sid, stk.ticker, stk.stock_name || stk.ticker)} disabled={busy}>🗑️</button>
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="ip-stk-tot-row">
                              <td className="ip-stk-name">
                                <button className="ip-add-stk-btn"
                                  onClick={() => { setAddStkPopup({ account_name: selAcct, sector_code_id: sid, sector_name: sec.sector_name }); setAddStkForm({ ticker: '', weight: '100' }); }}
                                  disabled={busy}>
                                  + 종목 추가
                                </button>
                              </td>
                              <td>{Math.round(sec.weight_total) || ''}</td>
                              <td>{fNum(sec.stocks.reduce((s, st) => s + st.plan_amount, 0))}</td>
                              <td>{fNum(sec.stocks.reduce((s, st) => s + st.current_amount, 0))}</td>
                              <td>100%</td>
                              <td>{fNum(sec.stocks.reduce((s, st) => s + st.diff_amount, 0))}</td>
                              <td>{fPctTarget(
                                sec.stocks.reduce((s, st) => s + st.current_amount, 0),
                                sec.stocks.reduce((s, st) => s + st.plan_amount, 0)
                              )}</td>
                              <td colSpan={2}></td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {computedSectors.length > 0 && (
                  <tfoot>
                    <tr className="ip-tot-row">
                      <td>합계</td>
                      <td>{fPct(ratioTotal)}%</td>
                      <td>{fNum(planAmtTotal)}</td>
                      <td>{fNum(currAmtTotal)}</td>
                      <td></td>
                      <td>{fNum(diffAmtTotal)}</td>
                      <td>{fPctTarget(currAmtTotal, planAmtTotal)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            <div className="ip-bottom-actions">
              <button className="ip-add-sec-btn" onClick={openAddSector} disabled={busy}>+ 섹터 추가</button>
              {isAdmin && (
                <button className="ip-admin-btn" onClick={handleInitDefault} disabled={busy}>
                  기본 데이터 생성 (user1)
                </button>
              )}
            </div>
          </>
        )}
      </>
    );
  };

  // ── 좌측: 전체 통합 그리드 ──
  const renderSummaryGrid = () => {
    if (loading)  return <div className="ip-empty">조회 중...</div>;
    if (!summary) return <div className="ip-empty">로드 중...</div>;

    const secs      = summary.sectors || [];
    const total     = summary.total_amount || 0;
    const planTotal = secs.reduce((s, sec) => s + sec.plan_amount, 0);
    const currTotal = secs.reduce((s, sec) => s + sec.current_amount, 0);
    const diffTotal = secs.reduce((s, sec) => s + (sec.diff_amount || 0), 0);

    return (
      <>
        <div className="ip-controls" style={{ marginTop: 0 }}>
          <div className="ip-ctrl-right" style={{ flex: 'none' }}>
            <span className="ip-ctrl-item">전체 투자금액 <strong>{fNum(total)}원</strong></span>
            <span className="ip-ctrl-item">섹터수 <strong>{secs.length}개</strong></span>
          </div>
        </div>

        <div className="ip-grid-wrap">
          <table className="ip-grid">
            <colgroup>
              <col style={{ width: col1Width }} />
              <col style={{ width: 88 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 82 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 100 }} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', position: 'relative' }}>
                  섹터명
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, cursor: 'col-resize', userSelect: 'none' }}
                       onMouseDown={handleCol1Resize} />
                </th>
                <th>목표비율%</th>
                <th>목표금액</th>
                <th>현재금액</th>
                <th>현투자비율</th>
                <th>차이금액</th>
                <th>목표대비투자비율</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {secs.length === 0 && (
                <tr><td colSpan={8} className="ip-empty">데이터가 없습니다.</td></tr>
              )}
              {secs.map(sec => {
                const sid        = sec.sector_code_id;
                const isExp      = expandedSum.has(sid);
                const secCurrAmt = sec.current_amount || 0;
                return (
                  <React.Fragment key={sid}>
                    <tr className={`ip-sec-row${isExp ? ' expanded' : ''}`}>
                      <td onClick={() => toggleSum(sid)} style={{ cursor: 'pointer' }}>
                        {isExp ? '▼' : '▶'} {sec.sector_name}
                      </td>
                      <td>{fPct(sec.plan_ratio)}</td>
                      <td>{fNum(sec.plan_amount)}</td>
                      <td>{fNum(sec.current_amount)}</td>
                      <td>{fPct(sec.current_ratio)}</td>
                      <td>{fNum(sec.diff_amount)}</td>
                      <td>{fPctTarget(sec.current_amount, sec.plan_amount)}</td>
                      <td></td>
                    </tr>
                    {isExp && (sec.stocks || []).map(stk => {
                      const inSec = secCurrAmt > 0
                        ? parseFloat((stk.current_amount / secCurrAmt * 100).toFixed(1))
                        : 0;
                      return (
                        <tr key={stk.ticker} className="ip-stk-row">
                          <td className="ip-stk-name">
                            <span className="ip-stk-indent">└</span>
                            {stk.stock_name || stk.ticker}
                            <span style={{ color: '#999', fontSize: 10, marginLeft: 4 }}>({stk.ticker})</span>
                          </td>
                          <td></td>
                          <td>{fNum(stk.plan_amount)}</td>
                          <td>{fNum(stk.current_amount)}</td>
                          <td>{fPct(inSec)}</td>
                          <td>{fNum(stk.diff_amount)}</td>
                          <td>{fPctTarget(stk.current_amount, stk.plan_amount)}</td>
                          <td></td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
            {secs.length > 0 && (
              <tfoot>
                <tr className="ip-tot-row">
                  <td>합계</td>
                  <td>{fPct(secs.reduce((s, sec) => s + (sec.plan_ratio || 0), 0))}</td>
                  <td>{fNum(planTotal)}</td>
                  <td>{fNum(currTotal)}</td>
                  <td></td>
                  <td>{fNum(diffTotal)}</td>
                  <td>{fPctTarget(currTotal, planTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </>
    );
  };

  // ── 렌더 ──
  return (
    <>
      <PageLayout title="투자계획등록" message={message}>
        {/* 탭 (최상단) */}
        <div className="ip-tabs">
          <button className={`ip-tab${activeTab === 'account' ? ' active' : ''}`} onClick={() => {
            setActiveTab('account');
            if (!userAdjusted) { leftWidthRef.current = 60; setLeftWidth(60); }
          }}>
            계좌별
          </button>
          <button className={`ip-tab${activeTab === 'summary' ? ' active' : ''}`} onClick={() => {
            setActiveTab('summary');
            if (!userAdjusted) { leftWidthRef.current = 45; setLeftWidth(45); }
          }}>
            전체 통합 현황
          </button>
        </div>

        {/* 좌우 분할 */}
        <div ref={containerRef} className={`ip-split-container${isDragging ? ' dragging' : ''}`}>
          {/* 좌측: 그리드 */}
          <div className="ip-split-left" style={{ width: `${leftWidth}%` }}>
            {activeTab === 'account' ? renderAccountGrid() : renderSummaryGrid()}
          </div>

          {/* 구분선 */}
          <div
            className={`ip-split-resizer${isDragging ? ' dragging' : ''}`}
            onMouseDown={handleResizerMouseDown}
          />

          {/* 우측: 그래프 */}
          <div className="ip-split-right">
            {renderChartPanel()}
          </div>
        </div>
      </PageLayout>

      {/* 섹터 추가 팝업 */}
      {addSecOpen && (
        <div className="ip-popup-overlay" onClick={() => setAddSecOpen(false)}>
          <div className="ip-popup" onClick={e => e.stopPropagation()}>
            <h3>섹터 추가</h3>
            <label>섹터</label>
            <select value={addSecForm.sector_code_id} onChange={e => setAddSecForm(p => ({ ...p, sector_code_id: e.target.value }))}>
              <option value="">선택</option>
              {availSecs.map(c => <option key={c.code_id} value={c.code_id}>{c.code_name}</option>)}
            </select>
            <label>목표비율 (%)</label>
            <input
              type="number" min="0" max="100" step="0.1"
              value={addSecForm.plan_ratio}
              onChange={e => setAddSecForm(p => ({ ...p, plan_ratio: e.target.value }))}
              placeholder="0"
            />
            <div className="ip-popup-actions">
              <button className="btn btn-sm btn-outline" onClick={() => setAddSecOpen(false)} disabled={busy}>취소</button>
              <button className="btn btn-sm btn-primary" onClick={handleAddSector} disabled={busy}>추가</button>
            </div>
          </div>
        </div>
      )}

      {/* 종목 추가 팝업 */}
      {addStkPopup && (
        <div className="ip-popup-overlay" onClick={() => setAddStkPopup(null)}>
          <div className="ip-popup" onClick={e => e.stopPropagation()}>
            <h3>{addStkPopup.sector_name} — 종목 추가</h3>
            <label>종목</label>
            <select value={addStkForm.ticker} onChange={e => setAddStkForm(p => ({ ...p, ticker: e.target.value }))}>
              <option value="">선택</option>
              {availableStocks.map(s => (
                <option key={s.ticker} value={s.ticker}>{s.stock_name} ({s.ticker})</option>
              ))}
            </select>
            <label>가중치 (기본 100)</label>
            <input
              type="number" min="1" step="1"
              value={addStkForm.weight}
              onChange={e => setAddStkForm(p => ({ ...p, weight: e.target.value }))}
            />
            <div className="ip-popup-actions">
              <button className="btn btn-sm btn-outline" onClick={() => setAddStkPopup(null)} disabled={busy}>취소</button>
              <button className="btn btn-sm btn-primary" onClick={handleAddStock} disabled={busy}>추가</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvestPlanPage;
